import React, { useEffect, useState, useRef, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { parentApi } from "../lib/api";
import { pushNote } from "../lib/notify";
import Paywall from "./Paywall";
import TrialNudge from "./TrialNudge";
import { BrandLogo } from "./Mascot";
import Icon from "./Icon";
import { Button } from "./ui";

const TABS = [
  { to: "/app", label: "Home", icon: "house", end: true },
  { to: "/app/chores", label: "Chores", icon: "list-checks" },
  { to: "/app/inbox", label: "Inbox", icon: "inbox" },
  { to: "/app/rewards", label: "Treats", icon: "gift" },
  { to: "/app/kiosk", label: "Kiosk", icon: "tablet-smartphone" },
];

export default function ParentLayout() {
  const { family, logout, isDemo, canEdit, isOwner, isPaused, loadFamily } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(0);
  const prevPending = useRef(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const settingsOpen = location.pathname === "/app/settings";
  const familyId = family?.id;

  useEffect(() => {
    if (isPaused && isOwner && !settingsOpen) setShowPaywall(true);
    if (!isPaused || settingsOpen) setShowPaywall(false);
  }, [isPaused, isOwner, settingsOpen]);

  const refreshBadge = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        parentApi.get("/chores", { params: { status: "pending" } }),
        parentApi.get("/redemptions", { params: { status: "pending" } }),
      ]);
      const total = c.data.chores.length + r.data.redemptions.length;
      if (prevPending.current !== null && total > prevPending.current) {
        const diff = total - prevPending.current;
        pushNote("ChoreMouse", `${diff} new item${diff > 1 ? "s" : ""} waiting for your approval.`);
      }
      prevPending.current = total;
      setPending(total);
    } catch (e) { console.error("refreshBadge error", e); }
  }, []);

  useEffect(() => {
    if (!familyId || isPaused) { setPending(0); return; }
    refreshBadge(); const t = setInterval(refreshBadge, 15000); return () => clearInterval(t);
  }, [refreshBadge, isPaused, familyId]);

  const onTrial = family?.subscription_status === "trial";
  const needsUpgrade = !isDemo && !isPaused && isOwner && onTrial;

  if (!family) return <div className="min-h-screen grid place-items-center bg-canvas" role="status">Loading your family…</div>;

  return (
    <div className="min-h-screen bg-parentzone">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/70">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/app")} className="flex items-center gap-2" data-testid="brand-home">
            <BrandLogo size={38} />
            <div className="text-left leading-tight">
              <div className="font-heading font-black text-ink text-lg">ChoreMouse</div>
              <div className="text-[11px] text-ink-muted font-semibold -mt-1">{family?.name || "Family"}</div>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/app/settings")} className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center hover:bg-slate-200 transition-colors" data-testid="settings-btn" aria-label="Settings">
              <Icon name="settings" className="text-ink-soft" width={18} height={18} />
            </button>
            <button onClick={logout} className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center hover:bg-coral/20 transition-colors" data-testid="logout-btn" aria-label="Log out">
              <Icon name="log-out" className="text-ink-soft" width={18} height={18} />
            </button>
          </div>
        </div>
      </header>

      {isDemo && (
        <button onClick={() => navigate("/register")} data-testid="demo-ribbon"
          className="w-full bg-gradient-to-r from-sunny to-[#FFD54A] text-sunny-fg text-sm font-bold py-2.5 px-5 flex items-center justify-center gap-2 hover:brightness-105 transition-[filter]">
          <Icon name="sparkles" width={16} height={16} />
          You're exploring the demo — start a free 7-day trial to save your own family
          <Icon name="arrow-right" width={16} height={16} />
        </button>
      )}
      {!isDemo && !isPaused && !canEdit && (
        <div data-testid="viewonly-ribbon"
          className="w-full bg-ink text-white/90 text-sm font-bold py-2.5 px-5 flex items-center justify-center gap-2">
          <Icon name="eye" width={16} height={16} />
          View-only access — ask the family owner to give you edit rights.
        </div>
      )}

      {needsUpgrade && (
        <button onClick={() => setShowPaywall(true)} data-testid="trial-banner"
          className="w-full bg-gradient-to-r from-primary-dark to-primary text-white text-sm font-bold py-2.5 px-5 flex items-center justify-center gap-2 hover:brightness-105 transition-[filter]">
          <Icon name="crown" width={16} height={16} />
          {family?.trial_days_left} {family?.trial_days_left === 1 ? "day" : "days"} left in your trial — choose your family plan
          <Icon name="arrow-right" width={16} height={16} />
        </button>
      )}

      <main className="max-w-3xl mx-auto px-5 pt-6 pb-28">
        {isPaused && !settingsOpen ? (
          <section className="rounded-4xl bg-white border-2 border-primary/20 p-6 sm:p-10 text-center shadow-clay" data-testid="account-paused">
            <div className="mx-auto grid place-items-center w-20 h-20 rounded-3xl bg-primary/10 text-primary mb-5"><Icon name="pause" width={36} height={36} /></div>
            <p className="text-xs font-black uppercase tracking-widest text-primary-dark">Progress saved. Adventure on pause.</p>
            <h1 className="font-heading font-black text-3xl text-ink mt-2">{family.access_reason === "trial_expired" || family.subscription_status === "trial" ? "Your 7-day trial has ended" : "Let's restart your family adventure"}</h1>
            <p className="text-ink-soft font-semibold mt-3 mb-6">{isOwner ? "Enroll to continue with every kid, mission, reward and earned coin right where you left them." : "Ask the family owner to enroll. Your kids, missions and rewards are safely saved."}</p>
            {isOwner && <Button className="w-full sm:w-auto" size="lg" onClick={() => setShowPaywall(true)} data-testid="paused-enroll"><Icon name="rocket" width={20} height={20} /> Enroll & continue</Button>}
            <div className="flex flex-wrap items-center justify-center gap-4 mt-5">
              <button onClick={() => navigate("/app/settings")} className="text-sm font-bold text-primary-dark underline" data-testid="paused-settings">Account & billing settings</button>
              <button disabled={refreshing} onClick={async () => { setRefreshing(true); try { await loadFamily(); } catch (e) { /* Keep the saved pause screen on connection failure. */ } finally { setRefreshing(false); } }} className="text-sm font-bold text-ink-soft underline disabled:opacity-50" data-testid="paused-refresh">{refreshing ? "Checking…" : "Refresh access"}</button>
            </div>
          </section>
        ) : <Outlet context={{ refreshBadge }} />}
      </main>

      {!isPaused && <nav className="fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-slate-200 safe-bottom" data-testid="bottom-nav">
        <div className="max-w-3xl mx-auto px-2 flex items-stretch justify-between">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={`tab-${t.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-bold transition-colors ${isActive ? "text-primary" : "text-ink-muted hover:text-ink-soft"}`}>
              {({ isActive }) => (
                <>
                  <div className={`relative grid place-items-center w-11 h-8 rounded-xl transition-colors ${isActive ? "bg-primary/10" : ""}`}>
                    <Icon name={t.icon} width={20} height={20} strokeWidth={2.4} />
                    {t.label === "Inbox" && pending > 0 && (
                      <span className="absolute -top-1 -right-0 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] grid place-items-center font-black" data-testid="inbox-badge">{pending}</span>
                    )}
                  </div>
                  {t.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>}

      <Paywall open={showPaywall} onClose={() => setShowPaywall(false)} />
      <TrialNudge />
    </div>
  );
}
